/* Explicit native preset bundles; no writes to the parent variant input. */
(() => {
  const option = (product, variant, name) => variant?.options[product.options.findIndex(o => o.toLowerCase() === name.toLowerCase())];
  const componentVariant = (product, size, color) => product?.variants.find(v => product.options.every((name, i) => name.toLowerCase() === 'size' ? v.options[i] === size : name.toLowerCase() === 'color' ? v.options[i] === color : false));
  function resolve(parent, parentVariant, offer, colors) {
    if (!parentVariant || !parentVariant.available) return {error: 'Select an available size and color first.'};
    const size = option(parent, parentVariant, 'Size');
    if (!size) return {error: 'Choose your size first.'};
    const values = {}, components = [];
    for (const c of offer.components) {
      if (!c.product) return {error: 'This bundle is currently unavailable.'};
      const isParent = String(c.product.id) === String(parent.id);
      let color = c.color;
      if (c.bundleOption) {
        color = isParent ? option(parent, parentVariant, 'Color') : colors[c.bundleOption];
        if (!color) return {error: 'Choose the companion color for this bundle.'};
        values[c.bundleOption] = color;
      }
      if (isParent && option(parent, parentVariant, 'Color') !== color) return {error: `This preset includes your item in ${color}. Select ${color} above, or choose Single item.`};
      const variant = componentVariant(c.product, size, color);
      if (!variant || !variant.available) return {error: `Not available in ${size} / ${color}. Choose another size or Single item.`};
      components.push({product: c.product, variant, color});
    }
    const variant = offer.product.variants.find(v => offer.product.options.every((name, i) => v.options[i] === (name.toLowerCase() === 'size' ? size : values[name])));
    if (!variant?.available) return {error: `This bundle is unavailable in ${size}. Choose another size or Single item.`};
    return {variant, components, size, regular: components.reduce((sum, c) => sum + c.variant.price, 0)};
  }
  window.LCPresetBundleResolve = resolve;
  if (customElements.get('lc-preset-bundles')) return;
  customElements.define('lc-preset-bundles', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized || this.closest('quick-add-modal')) return;
      this.form = document.getElementById(this.dataset.formId);
      this.root = this.closest('product-info');
      if (!this.form || !this.root) return;
      try { this.data = JSON.parse(this.querySelector('script[type="application/json"]').textContent); } catch (_) { return; }
      this.data.offers = this.data.offers.filter(o => o && o.product && o.components.every(c => c.product));
      if (!this.data.offers.length) return;
      this.initialized = true;
      this.selected = -1;
      this.colors = this.data.offers.map(() => ({}));
      this.buttons = [...this.form.querySelectorAll('button[type="submit"]')];
      this.nativeButtonState = this.buttons.map(button => ({disabled: button.disabled, text: button.querySelector('span')?.textContent}));
      this.wasActive = false;
      this.status = this.querySelector('.lc-preset-status');
      this.quantity = this.querySelector('.lc-preset-quantity input');
      this.hidden = false;
      this.form.lcPresetBundles = this;
      this.build();
      this.onChange = event => {
        const radio = event.target.closest('[data-lc-offer]');
        if (radio) this.selected = Number(radio.dataset.lcOffer);
        if (event.target.dataset.lcColor) this.colors[Number(event.target.dataset.offerIndex)][event.target.dataset.lcColor] = event.target.value;
        if (event.target.closest('variant-radios, variant-selects')) this.pending = true;
        this.refresh();
      };
      this.root.addEventListener('change', this.onChange);
      this.quantity.addEventListener('input', this.onChange);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, e => {
          if (String(e.data.sectionId) !== this.dataset.sectionId) return;
          this.pending = false;
          this.nativeButtonState = this.buttons.map(button => ({disabled: button.disabled, text: button.querySelector('span')?.textContent}));
          this.refresh();
        });
      }
      this.refresh();
    }
    disconnectedCallback() {
      this.root?.removeEventListener('change', this.onChange);
      this.quantity?.removeEventListener('input', this.onChange);
      this.unsubscribe?.();
      if (this.form?.lcPresetBundles === this) delete this.form.lcPresetBundles;
      this.initialized = false;
    }
    money(cents) { return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style:'currency',currency:this.data.currency || 'USD'}).format(cents / 100); }
    node(tag, text, className) {
      const el = document.createElement(tag);
      if (text !== undefined) el.textContent = text;
      if (className) el.className = className;
      return el;
    }
    build() {
      const choices = this.querySelector('.lc-preset-choices');
      choices.replaceChildren();
      this.cards = [];
      const addRadio = (index, title, container) => {
        const label = this.node('label', undefined, 'lc-preset-choice');
        const radio = document.createElement('input');
        radio.type = 'radio'; radio.name = `lc-offer-${this.dataset.sectionId}`;
        radio.dataset.lcOffer = index; radio.checked = index === -1;
        label.append(radio, this.node('strong', title)); container.append(label);
      };
      addRadio(-1, 'Single item', choices);
      this.data.offers.forEach((offer, index) => {
        const card = this.node('div', undefined, 'lc-preset-card');
        addRadio(index, offer.product.title, card);
        const photos = this.node('div', undefined, 'lc-preset-photos');
        const figures = [];
        offer.components.forEach(c => {
          const fig = this.node('figure');
          const img = this.node('img'); img.width = 110; img.height = 110; img.loading = 'lazy'; img.alt = c.product.title;
          img.src = c.product.image;
          const caption = this.node('figcaption', c.product.title);
          const detail = this.node('span', c.color, 'lc-preset-component-detail');
          fig.append(img, caption, detail); photos.append(fig); figures.push({img,detail,c});
          if (c.bundleOption && String(c.product.id) !== String(this.data.parent.id)) {
            const label = this.node('label', `${c.product.title} color`, 'lc-preset-color');
            const select = this.node('select'); select.dataset.lcColor = c.bundleOption; select.dataset.offerIndex = index;
            const prompt = this.node('option', 'Choose companion color'); prompt.value = ''; select.append(prompt);
            const optIndex = offer.product.options.indexOf(c.bundleOption);
            [...new Set(offer.product.variants.map(v => v.options[optIndex]))].filter(Boolean).forEach(value => {
              const opt = this.node('option', value); opt.value = value; select.append(opt);
            });
            label.append(select); card.append(label);
          }
        });
        card.append(photos);
        const pricing = this.node('p', undefined, 'lc-preset-pricing'); card.append(pricing);
        const reason = this.node('p', undefined, 'lc-preset-reason'); card.append(reason);
        choices.append(card); this.cards.push({card,figures,pricing,reason});
      });
    }
    parentVariant() { return this.data.parent.variants.find(v => String(v.id) === this.form.querySelector('[name="id"]').value); }
    state(index = this.selected) { return index < 0 ? null : resolve(this.data.parent, this.parentVariant(), this.data.offers[index], this.colors[index]); }
    refresh() {
      const parent = this.parentVariant();
      const size = option(this.data.parent, parent, 'Size');
      this.cards.forEach((ui, index) => {
        const offer = this.data.offers[index], state = this.state(index);
        ui.card.classList.toggle('is-selected', index === this.selected);
        ui.reason.textContent = state.error || `All ${offer.components.length} items in ${state.size}.`;
        ui.figures.forEach(({img,detail,c}) => {
          const color = c.bundleOption ? (String(c.product.id) === String(this.data.parent.id) ? option(this.data.parent,parent,'Color') : this.colors[index][c.bundleOption] || c.color) : c.color;
          const variant = componentVariant(c.product, size, color);
          // Variant image first; the fixed product image is only a fallback for the same default color.
          img.src = variant?.image || c.product.image;
          detail.textContent = `${color || ''}${size ? ' · '+size : ''}`;
        });
        const price = state.variant?.price ?? offer.product.variants[0]?.price;
        ui.pricing.replaceChildren(this.node('strong', this.money(price)));
        if (state.regular > price) ui.pricing.append(this.node('s', this.money(state.regular)), this.node('span', `Save ${this.money(state.regular - price)}`, 'lc-preset-save'));
      });
      const active = this.selected >= 0;
      this.querySelector('.lc-preset-quantity').hidden = !active;
      // Native quantity rules still run for singles; bundle quantity is a separate field.
      this.root.classList.toggle('lc-preset-active', active);
      const result = this.state();
      const count = Number(this.quantity.value);
      const invalidCount = !Number.isSafeInteger(count) || count < 1;
      this.status.textContent = active ? (this.pending ? 'Updating size and color…' : result.error || (invalidCount ? 'Enter a whole number of bundles, at least 1.' : `Quantity means bundles. ${count} bundle${count === 1 ? '' : 's'} = ${count * this.data.offers[this.selected].components.length} garments, all ${result.size}.`)) : '';
      this.buttons.forEach((button, index) => {
        if (button.classList.contains('loading')) return;
        if (!active && !this.wasActive) return;
        const disabled = active ? !!(this.pending || result.error || invalidCount) : (this.pending || this.nativeButtonState[index].disabled);
        button.disabled = disabled;
        const span = button.querySelector('span:not(.loading__spinner)');
        if (span) span.textContent = active ? `Add bundle to cart${result.variant && !invalidCount ? ' · ' + this.money(result.variant.price * count) : ''}` : this.nativeButtonState[index].text;
      });
      this.wasActive = this.buttons.some(button => button.classList.contains('loading')) ? (this.wasActive || active) : active;
    }
    applyToFormData(formData) {
      if (this.selected < 0) return true;
      const state = this.state(), count = Number(this.quantity.value);
      if (this.pending || state.error || !Number.isSafeInteger(count) || count < 1) return false;
      formData.set('id', String(state.variant.id));
      formData.set('quantity', String(count));
      return true;
    }
  });
})();
